import React, { FC, useEffect, useState } from 'react';
import { Col, Layout, Menu, Row, Tooltip } from 'antd';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { useMediaPredicate } from 'react-media-hook';
import { ArrowSquareOut, CaretDoubleLeft, SignOut } from 'common/svg';
import { nameBuilder } from 'common/helpers/name.helper';
import { Logo } from 'common/components/Logo';
import { getFirstMenuPath, getSidebarMenuItems, splitMenuPath, validateMenuPath } from 'common/helpers/sidebar.helper';
import { isFranchisorRole, isSuperAdminRole } from 'common/helpers/permission.helper';
import { ERoutesCommon, ERoutesFranchiseePrivate, RoutesCirPrefix } from 'common/models/routesModel';
import { MEDIA_MOBILE_768 } from 'common/const/common.const';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { logout } from 'app/store/reducers/auth.reducer';

const { Sider } = Layout;

export const Sidebar: FC = () => {
  const { authUser } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [openMenuKeys, setOpenMenuKeys] = useState<string[]>([]);
  const [selectedMenuKeys, setSelectedMenuKeys] = useState<string[]>([]);
  const [submenuHoverKey, setSubmenuHoverKey] = useState<string>('');
  const [menuItemHoverKey, setMenuItemHoverKey] = useState<string>('');
  const history = useHistory();
  const { segments } = useParams<{ segments: string | undefined }>();
  const isMobile: boolean = useMediaPredicate(MEDIA_MOBILE_768);

  useEffect(() => {
    const { submenuKeys, itemKey } = splitMenuPath(segments);

    setOpenMenuKeys((prev) => [...prev, ...submenuKeys]);
    setSelectedMenuKeys([itemKey]);
  }, [segments]);

  const userName = nameBuilder(authUser?.firstName, authUser?.lastName);
  // MBC-84 Phase 2: the CIR tree is permission-aware for franchisor/superadmin users.
  const menuItems = getSidebarMenuItems(collapsed, authUser?.role, authUser?.permissions);
  const isValidMenuPath = validateMenuPath(menuItems, segments);
  const isMbgoAdminUser = isSuperAdminRole(authUser?.role) || isFranchisorRole(authUser?.role);

  const onSubmenuHoverKeyChange = (key: string) => {
    setSubmenuHoverKey(key);
  };

  const onMenuItemHoverKeyChange = (key: string) => {
    setMenuItemHoverKey(key);
  };

  const onOpenMenuKeysChange = (keys: string[]) => {
    setOpenMenuKeys(keys);
  };

  const redirect = (keyPath: string[]) => {
    history.push(`${RoutesCirPrefix}/${keyPath.reverse().join('/')}`);
  };

  const onLogout = () => {
    dispatch(logout());
  };

  const redirectToMBGo = () => {
    // Franchisor/superadmin land on their own MBGo root — /store-list is a franchisee page.
    history.push(isMbgoAdminUser ? ERoutesCommon.Root : ERoutesFranchiseePrivate.StoreList);
  };

  const toggleCollapsed = () => {
    setCollapsed((prev) => !prev);
  };

  if (!isValidMenuPath) {
    // Franchisee fallback is unchanged (Generate PO). For franchisor/superadmin (MBC-84 Phase 2)
    // the target must exist in the permission-filtered tree; with no CIR modules at all the user
    // is sent back to the MBGo root instead of looping on a missing module.
    if (!isMbgoAdminUser) {
      return <Redirect to={`${RoutesCirPrefix}${ERoutesFranchiseePrivate.GeneratePO}`} />;
    }

    const firstMenuPath = getFirstMenuPath(menuItems);

    return firstMenuPath ? <Redirect to={`${RoutesCirPrefix}/${firstMenuPath}`} /> : <Redirect to={ERoutesCommon.Root} />;
  }

  if (isMobile) {
    return null;
  }

  return (
    <Sider className={`sidebar ${collapsed ? 'sidebar-collapsed' : ''}`}>
      <Row align="middle" wrap={false} className="sidebar__header">
        <Col>
          <Logo />
        </Col>

        <Col className="ff-medium fs-xs color-whiteDark sidebar__header-user" flex={1}>
          {userName}
        </Col>
      </Row>

      <Menu
        className="sidebar__body"
        theme="dark"
        mode="inline"
        inlineCollapsed={collapsed}
        openKeys={openMenuKeys}
        onOpenChange={onOpenMenuKeysChange}
        selectedKeys={selectedMenuKeys}
        onSelect={({ keyPath }) => redirect(keyPath)}
        items={menuItems.map((item) => {
          return {
            ...item,
            onMouseEnter: () => onSubmenuHoverKeyChange(item.key as string),
            onMouseLeave: () => onSubmenuHoverKeyChange(''),
            children: item.children?.map((children) => {
              return {
                ...children,
                icon: !collapsed ? (
                  children.icon
                ) : (
                  <Tooltip
                    overlayClassName={`sidebar__tooltip ${
                      menuItemHoverKey === children.key ? 'sidebar__tooltip-hover' : ''
                    }`}
                    title={children.label}
                    open={openMenuKeys.includes(item.key as string) && submenuHoverKey === item.key}
                    placement="right"
                    arrow={false}
                  >
                    {children.icon}
                  </Tooltip>
                ),
                onMouseEnter: () => onMenuItemHoverKeyChange(children.key as string),
                onMouseLeave: () => onMenuItemHoverKeyChange(''),
              };
            }),
          };
        })}
      />

      <Menu className="sidebar__footer" theme="dark" mode="inline" inlineCollapsed={collapsed}>
        <Menu.Item key="logout" className="sidebar__footer-logOut" icon={<SignOut />} onClick={onLogout}>
          Log out
        </Menu.Item>
        <Menu.Item key="mbgoModule" icon={<ArrowSquareOut />} onClick={redirectToMBGo}>
          MBGo Module
        </Menu.Item>
        <Menu.Item
          key="collapse"
          icon={collapsed ? <CaretDoubleLeft style={{ transform: 'rotate(180deg)' }} /> : <CaretDoubleLeft />}
          onClick={toggleCollapsed}
        >
          Collapse
        </Menu.Item>
      </Menu>
    </Sider>
  );
};
